import { useEffect, useState } from "react";
import { http, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Upload, Plus, Trash2, AlertTriangle, Wrench, CheckSquare, ChevronDown, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [{ v: "booking", l: "Booking.com" }, { v: "airbnb", l: "Airbnb" }];

export default function Recensioni() {
  const [apartments, setApartments] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [fApt, setFApt] = useState("all");
  const [toVerify, setToVerify] = useState([]);
  const [openCat, setOpenCat] = useState(null);
  const [imp, setImp] = useState(null);              // bozza import in anteprima
  const [importOpen, setImportOpen] = useState(false);
  const [platform, setPlatform] = useState("booking");
  const [impApt, setImpApt] = useState("");
  const [files, setFiles] = useState([]);
  const [archive, setArchive] = useState({});
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);        // recensione manuale
  const [action, setAction] = useState(null);        // {kind, review_id, preview}

  const load = async () => {
    const p = fApt !== "all" ? { apartment_id: fApt } : {};
    const [an, tv] = await Promise.all([
      http.get("/reviews/analysis", { params: p }),
      http.get("/reviews", { params: { needs_review: true } }),
    ]);
    setAnalysis(an.data); setToVerify(tv.data);
    const counts = {};
    (an.data.per_appartamento || []).forEach((a) => { counts[a.apartment_id] = a; });
    setArchive(counts);
  };
  useEffect(() => { http.get("/apartments").then((r) => setApartments(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fApt]);

  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "—";

  const doAnalyze = async () => {
    if (!impApt) return toast.error("Seleziona prima l'appartamento");
    if (!files.length) return toast.error("Seleziona uno o più file esportati");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("platform", platform);
      fd.append("apartment_id", impApt);
      files.forEach((f) => fd.append("files", f));
      const { data } = await http.post("/reviews/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImp(data); setImportOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/review-imports/${imp.id}/commit`);
      toast.success(`Importate ${data.inserted} nuove recensioni · ${data.skipped} già presenti`);
      setImp(null); setFiles([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const assign = async (rid, aptId) => {
    await http.put(`/reviews/${rid}`, { apartment_id: aptId, save_mapping: true });
    toast.success("Appartamento assegnato · mapping salvato"); load();
  };
  const saveManual = async (e) => {
    e.preventDefault();
    try {
      await http.post("/reviews", { ...manual, apartment_id: manual.apartment_id || null, rating: manual.rating ? Number(manual.rating) : null });
      toast.success("Recensione salvata"); setManual(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openAction = async (kind, reviewId, category) => {
    const { data } = await http.get(`/reviews/${reviewId}/action-preview`, { params: { kind, category } });
    setAction({ kind, reviewId, preview: { ...data } });
  };
  const confirmAction = async () => {
    const p = action.preview;
    try {
      if (action.kind === "task") {
        await http.post(`/reviews/${action.reviewId}/create-task`, { title: p.title, apartment_id: p.apartment_id, due_date: p.due_date || null, priority: p.priority, notes: p.notes, source_label: p.source_label });
        toast.success("Todo creato");
      } else {
        await http.post(`/reviews/${action.reviewId}/create-maintenance`, { description: p.description, apartment_id: p.apartment_id, priority: p.priority, reported_date: p.reported_date, notes: p.notes, source_label: p.source_label });
        toast.success("Manutenzione creata");
      }
      setAction(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delReview = async (id) => { if (!window.confirm("Eliminare la recensione?")) return; await http.delete(`/reviews/${id}`); load(); };

  const tot = analysis?.totale;

  return (
    <div className="space-y-6" data-testid="recensioni-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Operativo</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Analisi recensioni</h1>
          <p className="text-sm text-slate-500 mt-1">Cosa migliorare, appartamento per appartamento. Nessun effetto economico.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManual({ platform: "booking", apartment_id: "", date: new Date().toISOString().slice(0, 10), rating: "", title: "", text: "" })} data-testid="new-review-btn" className="rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Recensione</Button>
          <Button onClick={() => setImportOpen(true)} data-testid="import-reviews-btn" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Upload className="h-4 w-4 mr-1" /> Importa recensioni</Button>
        </div>
      </div>

      <Card className="p-4 rounded-2xl flex flex-wrap gap-3 items-end">
        <div><Label>Appartamento</Label>
          <Select value={fApt} onValueChange={setFApt}><SelectTrigger className="w-[240px]" data-testid="rev-f-apt"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli appartamenti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
        </div>
        {tot && (
          <div className="flex gap-6 ml-auto">
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Recensioni</div><div className="font-display text-2xl font-extrabold" data-testid="rev-count">{tot.count}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Voto medio</div><div className="font-display text-2xl font-extrabold flex items-center gap-1" data-testid="rev-avg">{tot.avg_rating ?? "—"}{tot.avg_rating ? <Star className="h-4 w-4 text-amber-500" /> : null}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Da verificare</div><div className="font-display text-2xl font-extrabold text-amber-700" data-testid="rev-toverify">{analysis.da_verificare}</div></div>
          </div>
        )}
      </Card>

      {toVerify.length > 0 && (
        <Card className="rounded-2xl p-4 border-amber-200 bg-amber-50/50" data-testid="rev-verify-section">
          <div className="flex items-center gap-2 font-display font-bold text-amber-900"><AlertTriangle className="h-4 w-4" /> Da verificare ({toVerify.length})</div>
          <div className="text-xs text-amber-800 mb-3">Nessuna associazione automatica incerta: scegli tu l'appartamento, il collegamento verrà salvato per i prossimi import.</div>
          <div className="space-y-2">
            {toVerify.map((r) => (
              <div key={r.id} className="bg-white rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`rev-verify-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500">{r.platform} · {fmtDate(r.date)} · annuncio: <b>{r.listing_name || "non indicato"}</b>{r.listing_id ? ` (${r.listing_id})` : ""}</div>
                  <div className="text-sm text-slate-800 line-clamp-2">{r.text_it || r.text || r.negative_text || r.positive_text}</div>
                </div>
                <Select onValueChange={(v) => assign(r.id, v)}>
                  <SelectTrigger className="w-[200px]" data-testid={`rev-assign-${r.id}`}><SelectValue placeholder="Appartamento" /></SelectTrigger>
                  <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tot && tot.problems.length > 0 && (
        <Card className="rounded-2xl p-4" data-testid="rev-problems">
          <div className="font-display font-bold text-[var(--teal-deep)]">Cosa non va · problemi ricorrenti{fApt !== "all" ? ` · ${aptName(fApt)}` : ""}</div>
          <div className="text-xs text-slate-500 mb-3">Apri una categoria per vedere le segnalazioni originali e trasformarle in azione.</div>
          <div className="space-y-2">
            {tot.problems.map((p) => (
              <div key={p.category} className="border border-slate-200 rounded-xl" data-testid={`rev-problem-${p.category}`}>
                <button onClick={() => setOpenCat(openCat === p.category ? null : p.category)} className="w-full flex items-center gap-2 p-3 text-left" data-testid={`rev-problem-toggle-${p.category}`}>
                  {openCat === p.category ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="text-amber-600 font-bold">⚠</span>
                  <span className="font-semibold text-slate-800">{p.category}</span>
                  <Badge variant="outline" className="border-rose-300 text-rose-700 ml-auto">{p.count} segnalazioni</Badge>
                </button>
                {openCat === p.category && (
                  <div className="px-3 pb-3 space-y-2">
                    {p.reviews.map((r, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm" data-testid={`rev-problem-item-${p.category}-${i}`}>
                        <div className="text-xs text-slate-500">{fmtDate(r.date)} · {aptName(r.apartment_id)} · {r.platform}</div>
                        <div className="text-slate-800 italic">“{r.quote}”</div>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" className="rounded-lg" data-testid={`rev-to-task-${r.review_id}`} onClick={() => openAction("task", r.review_id, p.category)}><CheckSquare className="h-3.5 w-3.5 mr-1" /> Crea Todo</Button>
                          <Button size="sm" variant="outline" className="rounded-lg" data-testid={`rev-to-maint-${r.review_id}`} onClick={() => openAction("maintenance", r.review_id, p.category)}><Wrench className="h-3.5 w-3.5 mr-1" /> Crea manutenzione</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {tot && tot.positives.length > 0 && (
        <Card className="rounded-2xl p-4" data-testid="rev-positives">
          <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Cosa va bene · aspetti positivi</div>
          <div className="space-y-1 text-sm">{tot.positives.map((p) => (
            <div key={p.category} className="flex items-center gap-2"><span className="text-emerald-600 font-bold">✓</span><span className="text-slate-700">{p.category} apprezzata</span><span className="ml-auto text-slate-400 text-xs">{p.count} citazioni</span></div>
          ))}</div>
        </Card>
      )}

      {analysis?.per_appartamento?.length > 0 && fApt === "all" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rev-per-apt">
          {analysis.per_appartamento.map((a) => (
            <Card key={a.apartment_id} className="rounded-2xl p-4 cursor-pointer hover:border-[var(--teal-medium)]" onClick={() => setFApt(a.apartment_id)} data-testid={`rev-apt-${a.apartment_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-[var(--teal-deep)]">{a.apartment_name}</div>
                <div className="text-sm text-slate-500">{a.count} recensioni{a.avg_rating ? ` · media ${a.avg_rating}` : ""}</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {a.problems.slice(0, 5).map((p) => <Badge key={p.category} variant="outline" className="border-rose-200 text-rose-700">{p.category} · {p.count}</Badge>)}
                {a.problems.length === 0 && <span className="text-xs text-slate-400">Nessun problema rilevato</span>}
              </div>
              {a.trend.length > 1 && <div className="text-[11px] text-slate-500 mt-2">Andamento: {a.trend.slice(-6).map((t) => `${t.month} (${t.count}${t.avg ? `, ${t.avg}` : ""})`).join(" · ")}</div>}
            </Card>
          ))}
        </div>
      )}

      <ImportsList onChanged={load} />

      {/* Import: scelta piattaforma + file */}      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Importa recensioni</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>1. Appartamento (archivio di destinazione)</Label>
              <Select value={impApt} onValueChange={setImpApt}><SelectTrigger data-testid="import-apt"><SelectValue placeholder="Seleziona appartamento" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{archive[a.id] ? ` · ${archive[a.id].count} in archivio` : ""}</SelectItem>)}</SelectContent></Select>
              <div className="text-[11px] text-slate-500 mt-1">Tutte le recensioni dei file caricati verranno assegnate a questo appartamento.</div>
            </div>
            <div><Label>2. Piattaforma</Label>
              <Select value={platform} onValueChange={setPlatform}><SelectTrigger data-testid="import-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>3. File esportati (CSV o XLSX, anche più di uno)</Label>
              <Input type="file" multiple accept=".csv,.xlsx,.xls,text/csv" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="import-file" />
              <div className="text-[11px] text-slate-500 mt-1">Le colonne vengono riconosciute automaticamente dal file. Le recensioni già presenti nell'archivio non vengono duplicate: Booking e Airbnb confluiscono nello stesso archivio dell'appartamento.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Annulla</Button>
            <Button onClick={doAnalyze} disabled={busy} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="import-analyze">{busy ? "Leggo i file..." : "Anteprima"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anteprima import */}
      <Dialog open={!!imp} onOpenChange={() => setImp(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="import-preview">
          <DialogHeader><DialogTitle className="font-display text-2xl">Anteprima import · {imp?.apartment_name} · {imp?.platform}</DialogTitle></DialogHeader>
          {imp && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3 rounded-xl"><div className="text-[10px] uppercase text-slate-500 font-semibold">Trovate</div><div className="font-display text-2xl font-extrabold" data-testid="imp-found">{imp.summary.trovate}</div></Card>
                <Card className="p-3 rounded-xl"><div className="text-[10px] uppercase text-slate-500 font-semibold">Già presenti</div><div className="font-display text-2xl font-extrabold text-slate-500" data-testid="imp-dups">{imp.summary.gia_presenti}</div></Card>
                <Card className="p-3 rounded-xl"><div className="text-[10px] uppercase text-slate-500 font-semibold">Nuove</div><div className="font-display text-2xl font-extrabold text-emerald-700" data-testid="imp-new">{imp.summary.nuove}</div></Card>
                <Card className="p-3 rounded-xl"><div className="text-[10px] uppercase text-slate-500 font-semibold">Archivio dopo import</div><div className="font-display text-2xl font-extrabold" data-testid="imp-archive">{(imp.archivio_attuale || 0) + imp.summary.nuove}</div></Card>
              </div>
              <div className="text-xs text-slate-500" data-testid="imp-split">Archivio attuale {imp.apartment_name}: {imp.archivio_attuale || 0} recensioni · file: {(imp.files || []).join(", ")}</div>
              <div className="max-h-[40vh] overflow-y-auto border border-slate-200 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-500"><th className="p-2">Data</th><th className="p-2">Voto</th><th className="p-2">Testo</th><th className="p-2">Stato</th></tr></thead>
                  <tbody>
                    {imp.rows.map((r) => (
                      <tr key={r.row_id} className={`border-t border-slate-100 ${r.duplicate ? "opacity-50" : ""}`} data-testid={`imp-row-${r.row_id}`}>
                        <td className="p-2 whitespace-nowrap">{r.date || "—"}</td>
                        <td className="p-2">{r.rating ?? "—"}</td>
                        <td className="p-2 max-w-[420px] truncate">{r.text || r.negative_text || r.positive_text}</td>
                        <td className="p-2">{r.duplicate ? <Badge variant="outline" className="border-slate-300 text-slate-500">{r.duplicate === "esistente" ? "GIÀ PRESENTE" : "duplicata nel file"}</Badge> : <Badge variant="outline" className="border-emerald-300 text-emerald-700">nuova</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImp(null)} data-testid="imp-cancel">Annulla</Button>
            <Button onClick={commit} disabled={busy} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="imp-confirm">Conferma import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recensione manuale */}
      <Dialog open={!!manual} onOpenChange={() => setManual(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="manual-review-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuova recensione</DialogTitle></DialogHeader>
          {manual && (
            <form onSubmit={saveManual} className="space-y-3">
              <div><Label>Piattaforma</Label>
                <Select value={manual.platform} onValueChange={(v) => setManual({ ...manual, platform: v })}><SelectTrigger data-testid="mr-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Appartamento</Label>
                <Select value={manual.apartment_id || ""} onValueChange={(v) => setManual({ ...manual, apartment_id: v })}><SelectTrigger data-testid="mr-apt"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} /></div>
                <div><Label>Voto</Label><Input type="number" step="0.1" value={manual.rating} onChange={(e) => setManual({ ...manual, rating: e.target.value })} data-testid="mr-rating" /></div>
              </div>
              <div><Label>Testo</Label><Textarea rows={3} value={manual.text} onChange={(e) => setManual({ ...manual, text: e.target.value })} required data-testid="mr-text" /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setManual(null)}>Annulla</Button><Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="mr-submit">Salva</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Anteprima azione (Todo / Manutenzione) */}
      <Dialog open={!!action} onOpenChange={() => setAction(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="action-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{action?.kind === "task" ? "Crea Todo" : "Crea manutenzione"}</DialogTitle></DialogHeader>
          {action && (
            <div className="space-y-3">
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">Origine: <b>{action.preview.source_label}</b> · {action.preview.apartment_name || "nessun appartamento"}</div>
              {action.kind === "task" ? (
                <>
                  <div><Label>Titolo</Label><Input value={action.preview.title} onChange={(e) => setAction({ ...action, preview: { ...action.preview, title: e.target.value } })} data-testid="ap-title" /></div>
                  <div><Label>Scadenza</Label><Input type="date" value={action.preview.due_date || ""} onChange={(e) => setAction({ ...action, preview: { ...action.preview, due_date: e.target.value } })} /></div>
                </>
              ) : (
                <div><Label>Descrizione</Label><Input value={action.preview.description} onChange={(e) => setAction({ ...action, preview: { ...action.preview, description: e.target.value } })} data-testid="ap-description" /></div>
              )}
              <div><Label>Priorità</Label>
                <Select value={action.preview.priority} onValueChange={(v) => setAction({ ...action, preview: { ...action.preview, priority: v } })}>
                  <SelectTrigger data-testid="ap-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{["bassa", "media", "alta", "urgente"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Note</Label><Textarea rows={2} value={action.preview.notes || ""} onChange={(e) => setAction({ ...action, preview: { ...action.preview, notes: e.target.value } })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} data-testid="ap-cancel">Annulla</Button>
            <Button onClick={confirmAction} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="ap-confirm">Conferma e crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tot && tot.count === 0 && <div className="text-center text-slate-400 py-8">Nessuna recensione ancora. Importa un export Booking/Airbnb oppure inseriscine una manualmente.</div>}

      {fApt !== "all" && (
        <Card className="rounded-2xl p-4" data-testid="rev-list">
          <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Recensioni · {aptName(fApt)}</div>
          <ReviewList apartmentId={fApt} onAction={openAction} onDelete={delReview} />
        </Card>
      )}
    </div>
  );
}

function ImportsList({ onChanged }) {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await http.get("/review-imports"); setItems(data); };
  useEffect(() => { load(); }, []);
  const del = async (i) => {
    const { data: imp } = await http.get(`/review-imports/${i.id}/impact`);
    const alsoReviews = imp.recensioni_collegate > 0 &&
      window.confirm(`${imp.avviso}\n\nOK = elimina anche le ${imp.recensioni_collegate} recensioni importate.\nAnnulla = elimina solo il file di import, le recensioni restano in archivio.`);
    if (!window.confirm(`Sei sicuro di voler eliminare questo import (${imp.filename || "file"})?`)) return;
    const { data } = await http.delete(`/review-imports/${i.id}`, { params: { delete_reviews: alsoReviews } });
    toast.success(`Import eliminato${data.recensioni_eliminate ? ` · ${data.recensioni_eliminate} recensioni rimosse` : ""}`);
    load(); onChanged?.();
  };
  if (items.length === 0) return null;
  return (
    <Card className="rounded-2xl p-4" data-testid="rev-imports">
      <div className="font-display font-bold text-[var(--teal-deep)] mb-2">File di import</div>
      <div className="space-y-1 text-sm">
        {items.map((i) => (
          <div key={i.id} className="flex items-center gap-2 py-1 border-b border-slate-100 last:border-0" data-testid={`rev-import-${i.id}`}>
            <Badge variant="outline" className="text-[10px]">{i.platform}</Badge>
            <span className="truncate flex-1 text-slate-600">{i.filename}</span>
            <span className="text-xs text-slate-400">{i.rows} righe · {i.status}{i.inserted !== undefined ? ` · ${i.inserted} importate` : ""}</span>
            <button onClick={() => del(i)} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`rev-import-del-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ReviewText({ r, testid }) {
  const [orig, setOrig] = useState(false);
  const it = r.text_it || [r.positive_text_it, r.negative_text_it].filter(Boolean).join(" — ");
  const original = r.text || [r.positive_text, r.negative_text].filter(Boolean).join(" — ");
  const translated = r.translation_status === "tradotta" && it;
  return (
    <div data-testid={testid}>
      <div className="text-sm text-slate-700">{orig || !translated ? original : it}</div>
      <div className="flex items-center gap-2 mt-0.5">
        {translated && (
          <button onClick={() => setOrig(!orig)} className="text-[11px] text-[var(--teal-deep)] underline" data-testid={`review-toggle-${r.id}`}>
            {orig ? "Vedi traduzione italiana" : "Vedi originale"}
          </button>
        )}
        {r.translation_status === "non_disponibile" && <span className="text-[11px] text-amber-700">Traduzione non disponibile</span>}
        {translated && !orig && <span className="text-[11px] text-slate-400">tradotta in italiano</span>}
      </div>
    </div>
  );
}

function ReviewList({ apartmentId, onAction, onDelete }) {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get("/reviews", { params: { apartment_id: apartmentId } }).then((r) => setItems(r.data)); }, [apartmentId]);
  if (items.length === 0) return <div className="text-sm text-slate-400">Nessuna recensione.</div>;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className="border-b border-slate-100 last:border-0 pb-2" data-testid={`review-${r.id}`}>
          <div className="text-xs text-slate-500 flex flex-wrap gap-2 items-center">
            <Badge variant="outline" className="text-[10px]">{r.platform}</Badge>
            <span>{fmtDate(r.date)}</span>{r.rating != null && <span>voto {r.rating}</span>}
            {(r.categories || []).map((c) => <Badge key={c} variant="outline" className="text-[10px] border-slate-300">{c}</Badge>)}
            <button onClick={() => onDelete(r.id)} className="ml-auto p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          {r.title && <div className="text-sm font-semibold">{r.title}</div>}
          <ReviewText r={r} testid={`review-text-${r.id}`} />
          {(r.problems || []).length > 0 && (
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`review-task-${r.id}`} onClick={() => onAction("task", r.id)}><CheckSquare className="h-3.5 w-3.5 mr-1" /> Crea Todo</Button>
              <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`review-maint-${r.id}`} onClick={() => onAction("maintenance", r.id)}><Wrench className="h-3.5 w-3.5 mr-1" /> Crea manutenzione</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
